/**
 * Wspólny start toru probe (przeglądarkowego) - używany przez `<SsoProbe>`
 * (wejście anonimowe) i `<AdminSessionPoll>` (otwarta karta, w której właśnie
 * wykryto zalogowanie w panelu).
 *
 * Sam probe to nawigacja top-level na `/ISSO/authorize?prompt=none`: IdP odsyła
 * jednorazowy ticket, callback wymienia go na sesję frontu (cookie httpOnly).
 * Nawigacja jest konieczna, bo cookie sesji panelu (`SameSite=Lax`) jedzie
 * wyłącznie przy przejściu top-level, a cookie sesji frontu ma być httpOnly -
 * czyli musi je ustawić serwer w odpowiedzi, nie JavaScript.
 */

/** Nazwa cookie z jednorazowym `state` - musi zgadzać się z `/api/sso/callback`. */
export const SSO_STATE_COOKIE = "sso_state";

/** TTL cookie `state` w sekundach - probe to kilka natychmiastowych przeskoków. */
const SSO_STATE_TTL_SECONDS = 300;

/**
 * Bramka jednokrotnego uruchomienia w obrębie załadowanego dokumentu.
 *
 * Chroni przed dwoma niezależnymi problemami: React w trybie deweloperskim
 * (StrictMode) wywołuje efekty DWA RAZY, a `<SsoProbe>` i `<AdminSessionPoll>`
 * mogą chcieć wystartować probe niemal równocześnie. Bez bramki powstawałyby
 * dwa różne `state` - cookie zostawałoby z drugiego przebiegu, a nawigacja szła
 * z pierwszym, przez co callback widziałby rozjazd i traktował użytkownika jak
 * anonima (objaw: "front nie wykrywa zalogowania" mimo poprawnej sesji).
 */
let probeStarted = false;

/**
 * Odczyt wartości cookie po nazwie.
 *
 * @param name Nazwa cookie
 * @returns Wartość albo null gdy cookie nie istnieje
 */
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Uruchamia probe: zapisuje `state` (double-submit CSRF) i przechodzi na IdP.
 *
 * Metoda nie wraca w normalnym przebiegu - kończy się nawigacją. Powtórne
 * wywołanie w obrębie tego samego dokumentu jest ignorowane.
 *
 * @param apiBase Adres backendu Imperial (bez końcowego slasha lub z nim)
 * @returns true gdy nawigacja została zainicjowana
 */
export function startSsoProbe(apiBase: string): boolean {
  if (!apiBase || typeof window === "undefined") return false;
  if (probeStarted) return false;

  probeStarted = true;

  try {
    /* Cookie z poprzedniej, nieukończonej próby jest w pełni dobre do użycia -
       liczy się wyłącznie zgodność wartości w cookie i w URL-u. */
    let state = readCookie(SSO_STATE_COOKIE);

    if (!state) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      state = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${SSO_STATE_COOKIE}=${state}; Path=/; Max-Age=${SSO_STATE_TTL_SECONDS}; SameSite=Lax${secure}`;
    }

    const returnTo = window.location.pathname + window.location.search;
    const callback =
      window.location.origin + "/api/sso/callback?return_to=" + encodeURIComponent(returnTo);

    const authorize =
      apiBase.replace(/\/$/, "") +
      "/ISSO/authorize?prompt=none&return=" +
      encodeURIComponent(callback) +
      "&state=" +
      encodeURIComponent(state);

    window.location.replace(authorize);

    return true;
  } catch {
    /* fail-soft: bez probe (SSO po prostu nie zadziała dla tej wizyty).
       Zwalniamy bramkę - do nawigacji nie doszło, więc kolejna próba ma sens. */
    probeStarted = false;

    return false;
  }
}
